/**
 * Resolves NLE manifest entries to raw bytes for the standalone export path.
 *
 * Per-kind helpers (extractShotImage / extractShotVideo) and the audio blob
 * store + CDN fallback live here. The dispatcher resolveEntryBytesStandalone
 * is called by nleExport.ts once per manifest entry during the streaming zip.
 */

import type { TimelineProject, TimelineShot, AudioClip } from '@/types/timeline.ts';
import type { NLEManifestEntry } from './nleManifestBuilder.ts';
import { getBlob } from '@/previs/audioBlobStore.ts';

// ── Data URL → Uint8Array ──────────────────────────────────────────────

function dataUrlToBytes(dataUrl: string): Uint8Array | null {
  const match = dataUrl.match(/^data:[^;]+;base64,(.+)$/);
  if (!match) return null;
  const binary = atob(match[1]);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

// ── Blob URL → JPEG data URL via canvas ────────────────────────────────

function blobUrlToJpegBytes(blobUrl: string, maxDim: number): Promise<Uint8Array | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const scale = maxDim / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(null); return; }
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (!blob) { resolve(null); return; }
          blob.arrayBuffer().then(buf => resolve(new Uint8Array(buf))).catch(() => resolve(null));
        },
        'image/jpeg',
        0.92,
      );
    };
    img.onerror = () => resolve(null);
    img.src = blobUrl;
  });
}

// ── Extract media for a single shot ────────────────────────────────────

const MAX_IMAGE_DIM = 1920;

async function extractShotImage(shot: TimelineShot): Promise<Uint8Array | null> {
  const src = shot.thumbnailDataUrl;
  if (!src) return null;

  // data: URL — decode directly
  if (src.startsWith('data:')) {
    // If it's already JPEG, use as-is; otherwise re-encode via canvas
    if (src.startsWith('data:image/jpeg')) {
      return dataUrlToBytes(src);
    }
    // Re-encode PNG/other → JPEG via canvas
    return blobUrlToJpegBytes(src, MAX_IMAGE_DIM);
  }

  // blob: URL — render through canvas
  if (src.startsWith('blob:')) {
    return blobUrlToJpegBytes(src, MAX_IMAGE_DIM);
  }

  return null;
}

// ── Video blob extraction ─────────────────────────────────────────────

async function extractShotVideo(videoObjectUrl: string): Promise<Uint8Array | null> {
  try {
    const resp = await fetch(videoObjectUrl);
    if (!resp.ok) return null;
    return new Uint8Array(await resp.arrayBuffer());
  } catch {
    return null;
  }
}

// ── Audio entry type ──────────────────────────────────────────────────

export interface NLEAudioEntry {
  path: string;
  mime: string;
  channels: number;
  duration: number;
  clipId: number;
  absoluteStart: number;
  volume: number;
  isMuted: boolean;
  lane: number;
  bufferOffset: number;
  fadeInTime: number;
  fadeOutTime: number;
}

// ── Per-entry byte resolution (standalone path) ────────────────────────

/**
 * Resolves a single NLEManifestEntry to raw bytes using the standalone
 * (browser-side) data sources: audioBlobStore for audio, thumbnailDataUrl /
 * videoObjectUrl for shots. Returns null if the data is unavailable.
 *
 * Called by the standalone orchestrator (Task 6). The embedded equivalent
 * reads directly from SQLite and lives in a separate module.
 */
export async function resolveEntryBytesStandalone(
  entry: NLEManifestEntry,
  project: TimelineProject,
  audioClips: AudioClip[],
): Promise<Uint8Array | null> {
  switch (entry.kind) {
    case 'shotImage': {
      const shot = findShotById(project, entry.shotId);
      return shot ? extractShotImage(shot) : null;
    }
    case 'shotVideo': {
      const shot = findShotById(project, entry.shotId);
      return shot?.videoObjectUrl ? extractShotVideo(shot.videoObjectUrl) : null;
    }
    case 'audioClip': {
      const clip = audioClips.find((c) => c.id === entry.clipId);
      if (!clip) return null;
      const local = getBlob(entry.clipId);
      if (local) return local;
      // CDN fallback for evicted clips
      if (!clip.cdnUrl) return null;
      try {
        const resp = await fetch(clip.cdnUrl);
        if (!resp.ok) return null;
        return new Uint8Array(await resp.arrayBuffer());
      } catch {
        console.warn(`[NLE Export] Failed to fetch CDN audio for clip ${clip.id}`);
        return null;
      }
    }
  }
}

function findShotById(project: TimelineProject, shotId: number): TimelineShot | null {
  for (const scene of project.scenes) {
    for (const shot of scene.shots) {
      if (shot.id === shotId) return shot;
    }
  }
  return null;
}
